/**
 * Settings → AI & Workers → Who does what.
 *
 * One row for Overseer, Planner, and each Worker. Computer and AI
 * stay visible and default to Auto. Authority, Actual, success counts, move and
 * host lists, re-authorization, launch policy, and the other existing controls
 * sit behind that row's Advanced toggle. Nothing here adds a setting or deletes
 * a stored assignment.
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { Guard } from '../App';
import { MAIN_COMMAND_AI } from '../lib/command-ai';
import type {
  AiConnectionStatusRow,
  CommandPlannerHostStatus,
  LaunchCatalogRow,
  ModelOfferingRef,
  NodeOffering,
  NodePresence,
  RuntimeOutage,
  RuntimeProof,
  Worker,
  WorkerOfferingPreference,
  WorkforceSession,
} from '../lib/data';
import { useCommandPlannerHost, useGuardedMutation, useWorkerOffering } from '../lib/mutations';
import {
  loadManagementHosting,
  placementIsValid,
  setPlacementRequest,
  writeOnce,
  type HostingRead,
  type PlacementHost,
} from '../lib/management-hosting';
import { sendNudge } from '../lib/nudge';
import { offeringSourceLabel, resolveOfferingView } from '../lib/offering-resolve';
import { smokeTestCommand } from '../lib/plan-routing';
import { plannerHostOptions, type PlannerHostOption } from '../lib/planner-host-settings';
import {
  AUTO_VALUE,
  WHO_DOES_WHAT_ROLE_LABEL,
  placementComputerChanged,
  placementComputerValue,
  placementDraftForComputer,
  plannerAiValue,
  plannerChoiceResult,
  plannerComputerValue,
  workerAiValue,
  type WhoDoesWhatRole,
} from '../lib/who-does-what';
import { workerComputerLabel } from '../lib/workforce-display';
import {
  workerOfferingChoice,
  workerOfferingLabel,
  workerOfferingProof,
  workerOfferingProofLabel,
} from '../lib/worker-offering-choice';
import { LaunchPolicy } from './LaunchPolicy';
import { ManagementHostingSettings } from './ManagementHostingSettings';
import { PlannerHostSettings } from './PlannerHostSettings';

export interface WhoDoesWhatProps {
  readonly client: SupabaseClient;
  readonly guard: Guard;
  readonly orgId: string;
  readonly revision: number;
  readonly workers: readonly Worker[];
  readonly sessions: readonly WorkforceSession[];
  readonly nodes: readonly NodePresence[];
  readonly nodeOfferings: readonly NodeOffering[];
  readonly modelOfferings: readonly ModelOfferingRef[];
  readonly preferences: readonly WorkerOfferingPreference[];
  readonly runtimeProofs: readonly RuntimeProof[];
  readonly launchCatalog: readonly LaunchCatalogRow[];
  readonly nowMs: number;
  readonly plannerUrl: string;
  readonly onChanged: () => Promise<void>;
  readonly outages: readonly RuntimeOutage[];
  readonly connectionStatuses: readonly AiConnectionStatusRow[];
  readonly plannerStatus: CommandPlannerHostStatus | null;
}

function thinkingFor(offeringKey: string | null, catalog: readonly LaunchCatalogRow[]): string | null {
  if (offeringKey === null) return null;
  const runtime = offeringKey.split('/')[0];
  const row = catalog.find((item) => item.runtime_key === runtime);
  const effort = row?.env?.CLAUDE_CODE_EFFORT_LEVEL?.trim()
    || row?.env?.CODEX_REASONING_EFFORT?.trim()
    || null;
  return effort && effort !== '' ? effort : null;
}

export function WhoDoesWhat(props: WhoDoesWhatProps): ReactElement {
  const hosting = useHosting(props.client, props.orgId, props.revision);
  const plannerMutation = useCommandPlannerHost(props.client);
  const { setOffering, busy: offeringBusy, error: offeringError } = useWorkerOffering(props.client);
  const smokeMutation = useGuardedMutation(props.client);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [plannerDraft, setPlannerDraft] = useState<{ computer: string; ai: string; note: string | null } | null>(null);
  const plannerKey = `${props.plannerStatus?.node_id ?? ''}|${props.plannerStatus?.runtime_key ?? ''}|${props.plannerStatus?.fallback_mode ?? ''}`;
  useEffect(() => { setPlannerDraft(null); }, [plannerKey]);

  const plannerOptions = useMemo(() => plannerHostOptions({
    nodes: props.nodes,
    nodeOfferings: props.nodeOfferings,
    modelOfferings: props.modelOfferings,
    runtimeProofs: props.runtimeProofs,
    outages: props.outages,
    preferredRuntimeKey: MAIN_COMMAND_AI.model.replace(/-account-default$/, ''),
    nowMs: props.nowMs,
  }), [props.nodes, props.nodeOfferings, props.modelOfferings, props.runtimeProofs, props.outages, props.nowMs]);

  const reloadHosting = hosting.reload;
  const writePlacement = (role: string, placement: NonNullable<HostingRead['placements']>['roles'][number]['placement'], nodeId: string) => {
    if (!placementComputerChanged(placement, nodeId)) return;
    const draft = placementDraftForComputer(placement, nodeId);
    if (!placementIsValid(draft)) {
      setMessage('That computer choice is not a placement Brunel can store. Nothing was changed.');
      return;
    }
    void props.guard('post_owner_command', async () => {
      setBusy(true);
      setMessage(null);
      try {
        const outcome = await writeOnce(props.client, 'set_management_role_placement', setPlacementRequest(props.orgId, role, draft));
        setMessage(outcome.outcome === 'ok'
          ? 'Preference saved. The role has not moved.'
          : outcome.outcome === 'unknown'
            ? 'Brunel could not confirm the preference saved. It has been reread; it will not be retried automatically.'
            : `Brunel refused that hosting change (${outcome.code}).`);
        await reloadHosting();
      } finally {
        setBusy(false);
      }
    });
  };

  const applyPlanner = (computerId: string, runtimeKey: string) => {
    const pairAvailable = plannerOptions.some((option) =>
      option.nodeId === computerId && option.runtimeKey === runtimeKey && option.connected && option.available);
    const result = plannerChoiceResult({
      status: props.plannerStatus, computerId, runtimeKey, pairAvailable,
    });
    if (result.kind === 'hold') {
      setPlannerDraft({ computer: computerId, ai: runtimeKey, note: result.reason });
      return;
    }
    setPlannerDraft(null);
    void props.guard('post_owner_command', async () => {
      const outcome = await plannerMutation.save(result.kind === 'clear'
        ? { orgId: props.orgId, nodeId: null, runtimeKey: '', fallbackMode: result.fallbackMode, enabled: false }
        : { orgId: props.orgId, nodeId: result.nodeId, runtimeKey: result.runtimeKey, fallbackMode: result.fallbackMode, enabled: true });
      if (outcome === 'saved' || outcome === 'unknown') await props.onChanged();
    });
  };

  const hosts = hosting.read?.placements?.hosts ?? [];
  const placementFor = (role: string) => hosting.read?.placements?.roles.find((item) => item.role === role) ?? null;

  const controlsBusy = busy || plannerMutation.busy || offeringBusy;

  return (
    <div className="settings-block who-does-what">
      <h2>Who does what</h2>
      <p className="muted">
        Computer and AI start on Auto. Auto keeps the resolution already stored for that role.
        It does not choose a different provider, runtime, model, or route. An explicit assignment stays until you change it.
      </p>
      {message !== null && <p role="alert">{message}</p>}
      {offeringError !== null && <p role="alert">{offeringError}</p>}
      {plannerMutation.error !== null && <p role="alert">{plannerMutation.error}</p>}
      {smokeMutation.error !== null && <p role="alert">{smokeMutation.error}</p>}
      <div className="who-does-what-scroll">
        <table className="who-does-what-table">
          <thead>
            <tr>
              <th scope="col">Role</th>
              <th scope="col">Computer</th>
              <th scope="col">AI</th>
              <th scope="col">Advanced</th>
            </tr>
          </thead>
          <tbody>
            <RoleRow label={WHO_DOES_WHAT_ROLE_LABEL.overseer} role="overseer" busy={controlsBusy}
              computer={placementComputerValue(placementFor('overseer')?.placement)}
              hosts={hosts}
              onComputer={(nodeId) => {
                const role = placementFor('overseer');
                if (role !== null) writePlacement('overseer', role.placement, nodeId);
              }}
              ai={AUTO_VALUE}
              aiOptions={[{ value: AUTO_VALUE, label: 'Auto' }]}
              onAi={() => undefined}
              advanced={(
                <ManagementHostingSettings client={props.client} guard={props.guard} orgId={props.orgId}
                  revision={props.revision} onlyRoles={['overseer']} startOpen />
              )}
            />
            <PlannerRow {...props} options={plannerOptions} busy={controlsBusy}
              computer={plannerDraft?.computer ?? plannerComputerValue(props.plannerStatus)}
              ai={plannerDraft?.ai ?? plannerAiValue(props.plannerStatus)}
              note={plannerDraft?.note ?? null}
              onChange={applyPlanner}
            />
            {props.workers.map((worker) => (
              <WorkerRow key={worker.id} {...props} worker={worker} busy={controlsBusy}
                onAi={(nodeId, offeringKey) => {
                  void props.guard('post_owner_command', async () => {
                    if (await setOffering(props.orgId, worker.id, nodeId, offeringKey)) await props.onChanged();
                  });
                }}
                onSmoke={(session) => {
                  void props.guard('post_owner_command', async () => {
                    const result = await smokeMutation.submit('post_owner_command', {
                      orgId: props.orgId,
                      projectId: session.project_id,
                      content: smokeTestCommand(worker.number),
                    });
                    if (result.ok) {
                      sendNudge(props.plannerUrl);
                      await props.onChanged();
                    }
                  });
                }}
                smokeBusy={smokeMutation.busy}
              />
            ))}
            {props.workers.length === 0 && (
              <RoleRow label="Worker" role="worker" busy={controlsBusy}
                computer={AUTO_VALUE}
                hosts={[]}
                onComputer={() => undefined}
                ai={AUTO_VALUE}
                aiOptions={[{ value: AUTO_VALUE, label: 'Auto' }]}
                onAi={() => undefined}
                advanced={<p className="muted">no Worker yet</p>}
              />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function useHosting(client: SupabaseClient, orgId: string, revision: number): { read: HostingRead | null; reload: () => Promise<void> } {
  const [read, setRead] = useState<HostingRead | null>(null);
  const reload = useCallback(async () => {
    setRead(await loadManagementHosting(client, orgId));
  }, [client, orgId]);
  useEffect(() => { void reload(); }, [reload, revision]);
  return { read, reload };
}

interface AiOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

function RoleRow({ label, role, computer, hosts, onComputer, ai, aiOptions, onAi, advanced, busy }: {
  readonly label: string;
  readonly role: WhoDoesWhatRole | 'worker';
  readonly computer: string;
  readonly hosts: readonly PlacementHost[];
  readonly onComputer: (nodeId: string) => void;
  readonly ai: string;
  readonly aiOptions: readonly AiOption[];
  readonly onAi: (value: string) => void;
  readonly advanced: ReactNode;
  readonly busy: boolean;
}): ReactElement {
  const computerMissing = computer !== AUTO_VALUE && !hosts.some((host) => host.nodeId === computer);
  return (
    <tr data-who-role={role}>
      <th scope="row">{label}</th>
      <td>
        <select aria-label={`Computer for ${label}`} value={computer} disabled={busy}
          onChange={(event) => onComputer(event.target.value)}>
          <option value={AUTO_VALUE}>Auto</option>
          {computerMissing && <option value={computer}>Stored computer</option>}
          {hosts.map((host) => (
            <option key={host.nodeId} value={host.nodeId}>
              {host.name}{host.hostKind === 'ipad' ? ' (iPad)' : ''}{host.online ? '' : ' · offline'}
            </option>
          ))}
        </select>
      </td>
      <td>
        <select className="who-does-what-ai" aria-label={`AI for ${label}`} value={ai} disabled={busy}
          onChange={(event) => onAi(event.target.value)}>
          {aiOptions.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled === true}>{option.label}</option>
          ))}
        </select>
      </td>
      <td>
        <details className="who-does-what-advanced">
          <summary>Advanced for {label}</summary>
          {advanced}
        </details>
      </td>
    </tr>
  );
}

function PlannerRow(props: WhoDoesWhatProps & {
  readonly options: readonly PlannerHostOption[];
  readonly computer: string;
  readonly ai: string;
  readonly note: string | null;
  readonly busy: boolean;
  readonly onChange: (computerId: string, runtimeKey: string) => void;
}): ReactElement {
  const computers = new Map<string, string>();
  for (const option of props.options) computers.set(option.nodeId, option.nodeName);
  if (props.plannerStatus !== null && !computers.has(props.plannerStatus.node_id)) {
    computers.set(props.plannerStatus.node_id, props.plannerStatus.node_name);
  }
  const runtimes = props.options.filter((option) => option.nodeId === props.computer);
  const storedRuntime = props.plannerStatus !== null && props.plannerStatus.node_id === props.computer
    ? props.plannerStatus.runtime_key
    : null;
  return (
    <tr data-who-role="planner">
      <th scope="row">Planner</th>
      <td>
        <select aria-label="Computer for Planner" value={props.computer} disabled={props.busy}
          onChange={(event) => props.onChange(event.target.value, props.ai)}>
          <option value={AUTO_VALUE}>Auto</option>
          {[...computers.entries()].map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </td>
      <td>
        <select className="who-does-what-ai" aria-label="AI for Planner" value={props.ai} disabled={props.busy}
          onChange={(event) => props.onChange(props.computer, event.target.value)}>
          <option value={AUTO_VALUE}>Auto</option>
          {storedRuntime !== null && !runtimes.some((option) => option.runtimeKey === storedRuntime) && (
            <option value={storedRuntime} disabled>{props.plannerStatus?.runtime_key} · not currently offered</option>
          )}
          {runtimes.map((option) => (
            <option key={option.runtimeKey} value={option.runtimeKey} disabled={!option.connected || !option.available}>
              {option.runtimeLabel}{option.connected && option.available ? '' : ' · unavailable'}
            </option>
          ))}
        </select>
        {props.note !== null && <small role="note">{props.note}</small>}
      </td>
      <td>
        <details className="who-does-what-advanced">
          <summary>Advanced for Planner</summary>
          <PlannerHostSettings
            client={props.client} guard={props.guard} orgId={props.orgId}
            status={props.plannerStatus} nodes={props.nodes} nodeOfferings={props.nodeOfferings}
            modelOfferings={props.modelOfferings} runtimeProofs={props.runtimeProofs} outages={props.outages}
            preferredRuntimeKey={MAIN_COMMAND_AI.model.replace(/-account-default$/, '')}
            nowMs={props.nowMs} onChanged={props.onChanged}
          />
          <LaunchPolicy client={props.client} guard={props.guard} orgId={props.orgId}
            rows={props.launchCatalog} onChanged={props.onChanged} />
        </details>
      </td>
    </tr>
  );
}

function WorkerRow(props: WhoDoesWhatProps & {
  readonly worker: Worker;
  readonly busy: boolean;
  readonly smokeBusy: boolean;
  readonly onAi: (nodeId: string, offeringKey: string | null) => void;
  readonly onSmoke: (session: WorkforceSession) => void;
}): ReactElement {
  const choice = workerOfferingChoice({
    worker: props.worker,
    sessions: props.sessions,
    nodes: props.nodes,
    nodeOfferings: props.nodeOfferings,
    modelOfferings: props.modelOfferings,
    preferences: props.preferences,
    runtimeProofs: props.runtimeProofs,
    nowMs: props.nowMs,
  });
  const label = choice === null
    ? `Worker ${props.worker.number}`
    : workerComputerLabel(props.worker.number, props.worker.name, choice.node?.name ?? 'unknown computer');
  const ai = workerAiValue(choice?.preferenceKey ?? null);
  const effective = choice === null ? null : resolveOfferingView({
    workerPreferenceKey: choice.preferenceKey,
    nodeOfferingKeys: choice.available,
    autoOfferingKey: choice.session.offering_key,
  });
  const proofKey = effective?.offeringKey ?? choice?.session.offering_key ?? null;
  const proof = choice === null || proofKey === null
    ? null
    : workerOfferingProof(proofKey, choice.nodeId, props.modelOfferings, props.runtimeProofs);
  const sessionProof = choice === null
    ? null
    : workerOfferingProof(choice.session.offering_key, choice.nodeId, props.modelOfferings, props.runtimeProofs);
  const thinking = thinkingFor(proofKey, props.launchCatalog);
  return (
    <tr data-who-role={`worker-${props.worker.number}`}>
      <th scope="row">
        {label}
        {choice?.offline === true && <span className="tag">offline</span>}
      </th>
      <td>
        <select aria-label={`Computer for Worker ${props.worker.number}`} value={AUTO_VALUE} disabled={props.busy}
          onChange={() => undefined}>
          <option value={AUTO_VALUE}>Auto</option>
        </select>
      </td>
      <td>
        {choice === null ? (
          <select className="who-does-what-ai" aria-label={`AI for Worker ${props.worker.number}`} value={AUTO_VALUE} disabled>
            <option value={AUTO_VALUE}>Auto</option>
          </select>
        ) : (
          <select className="who-does-what-ai" aria-label={`AI for Worker ${props.worker.number}`} value={ai}
            disabled={props.busy || choice.working}
            onChange={(event) => props.onAi(choice.nodeId, event.target.value === '' ? null : event.target.value)}>
            <option value={AUTO_VALUE}>Auto</option>
            {choice.options.map((option) => (
              <option key={option.key} value={option.key}>
                {workerOfferingLabel(option.key, props.modelOfferings)}
                {option.available ? '' : ' — unavailable on this computer'}
              </option>
            ))}
          </select>
        )}
      </td>
      <td>
        <details className="who-does-what-advanced">
          <summary>Advanced for Worker {props.worker.number}</summary>
          {choice === null ? <p className="muted">no active session</p> : (
            <>
              <small className="muted">
                Auto uses Brunel&apos;s run history. A pin is exact: if that offering is not on the computer, Brunel blocks dispatch rather than silently substituting another runtime.
              </small>
              <small className="muted">
                Effective: {effective?.offeringKey ? workerOfferingLabel(effective.offeringKey, props.modelOfferings) : 'none'}
                {' · '}{effective ? offeringSourceLabel(effective.source) : ''}
                {effective?.ok
                  ? ` · ${workerOfferingProofLabel(proof)}`
                  : ' · pinned offering unavailable — dispatch blocked (no Auto substitute)'}
              </small>
              <small className="muted">Session: {workerOfferingLabel(choice.session.offering_key, props.modelOfferings)}{choice.offline ? ' · offline' : ''}</small>
              {thinking !== null && <small className="muted">Thinking: {thinking} (catalog; not an automatic substitute)</small>}
              {sessionProof && !sessionProof.proven && sessionProof.last_failure_summary
                ? <small className="muted">Last failure: {sessionProof.last_failure_summary}</small>
                : null}
              <div className="row">
                <button type="button" disabled={choice.offline || choice.working || props.busy || props.smokeBusy}
                  onClick={() => props.onSmoke(choice.session)}>
                  Smoke test selected AI
                </button>
                <small className="muted">Creates one read-only Brunel task; approve the plan, then this runtime&apos;s proof updates from the real run.</small>
              </div>
            </>
          )}
        </details>
      </td>
    </tr>
  );
}
